import { useMemo } from 'react';
import type { ActivityLine } from '@features/billing/components/balance-table/rows';
import { usePatientFinancialSummaryQuery } from '@features/billing/store/v2-api';
import type { ChargeLinePreviewResponse } from '@store/generated/billing-v2';

import type {
  EncounterLedgerGroup,
  LedgerEntryItem,
  PatientInvoiceHistoryItem,
} from '../types';

import { useChargeLinePreviewTarget } from './usePreviewByEncounter';

interface UseLedgerDataArgs {
  patientId: string;
  encounterId?: string | null;
  chargeLineIds?: string[];
}

interface UseLedgerData {
  isLoading: boolean;
  /**
   * The charge-line preview specifically (gdn-2957).
   *
   * Kept separate from `isLoading` because the two differ: the preview is
   * an RTK mutation fired from an effect, and patient scope leaves it out
   * of `isLoading` altogether. Anything reserving space for preview-derived
   * content has to watch this, or it releases the space too early.
   */
  isPreviewLoading: boolean;
  patientName?: string | null;
  /**
   * Total balance to surface in the hero. In encounter scope this prefers
   * the preview's `total_due_cents` (authoritative — clamped, includes
   * unallocated credits). Falls back to the encounter group's
   * `patient_balance_cents` while preview is loading. In patient scope
   * this is the patient-wide ledger total.
   */
  balanceCents: number;
  /** Realized ledger entries in the active scope. Sorted by occurred_at desc. */
  entries: Array<LedgerEntryItem & { encounterLabel?: string | null }>;
  /** Collapsed lifecycle lines for the active encounter (gdn-2941) —
   * the gated drawer renders these instead of raw entries. Empty in
   * patient scope and on payloads predating the field. */
  activityLines: ActivityLine[];
  /** Encounter row for the active encounter (encounter scope only). */
  encounter?: EncounterLedgerGroup;
  /**
   * Per-line preview for the active encounter. Powers the Receipt section
   * (CPT, description, patient_due, applied_payment, already_invoiced) and
   * the SettleSheet defaults (suggested_reason).
   */
  preview?: ChargeLinePreviewResponse;
  /** Open invoice IDs on this patient — used for the "Remind invoice" method. */
  openInvoiceIds: string[];
  /**
   * Open invoices on this patient (status === 'open'). Encounter scope
   * filters to invoices linked to the active encounter; patient scope
   * surfaces all open invoices. Used by the Open invoices section in the
   * Ledger tab.
   */
  openInvoices: PatientInvoiceHistoryItem[];
  staleInvoiceIds: string[];
}

/**
 * Composes the patient financial summary + per-encounter charge-line
 * preview into the shape the drawer renders.
 *
 * - **Encounter scope**: balance from preview's `total_due_cents` (or the
 *   encounter group's `patient_balance_cents` while loading). Entries
 *   filtered to the encounter. Preview surfaces a per-line Receipt section.
 * - **Patient scope**: balance from `summary.total_balance_cents`. Entries
 *   are all encounters' entries + account_level_entries with an encounter
 *   label attached. Preview is not fetched (no patient-wide forecast yet).
 */
export function useLedgerData({
  patientId,
  encounterId,
  chargeLineIds,
}: UseLedgerDataArgs): UseLedgerData {
  const { data, isLoading } = usePatientFinancialSummaryQuery(
    { patientId },
    { skip: !patientId },
  );

  // The financial summary is RTK-Query-tagged and refetches automatically
  // after captureCharges / recordPayment / writeOff. The preview is a
  // mutation, so it doesn't participate in tag-based invalidation —
  // bridge it by deriving a key from the encounter's entry signature
  // (count + last entry id) so a fresh entry triggers a preview refetch.
  const previewRefreshKey = (() => {
    const entries = encounterId
      ? (data?.encounters ?? []).find((e) => e.encounter_id === encounterId)?.entries ?? []
      : [
          ...(data?.account_level_entries ?? []),
          ...(data?.encounters ?? []).flatMap((enc) => enc.entries ?? []),
        ];
    const lastId = entries.length > 0 ? entries[entries.length - 1].entry_id : '';
    return `${entries.length}:${lastId}`;
  })();
  const { data: preview, isLoading: isPreviewLoading } = useChargeLinePreviewTarget(
    chargeLineIds && chargeLineIds.length > 0
      ? { chargeLineIds }
      : encounterId
        ? { encounterId }
        : { patientId },
    previewRefreshKey,
  );

  const result = useMemo<UseLedgerData>(() => {
    if (!data) {
      return {
        isLoading: isLoading || isPreviewLoading,
      isPreviewLoading,
        balanceCents: 0,
        entries: [],
        activityLines: [],
        openInvoiceIds: [],
        openInvoices: [],
        staleInvoiceIds: [],
        preview,
      };
    }

    const allOpenInvoices = (data.invoices ?? []).filter((inv) => inv.status === 'open');

    if (encounterId) {
      const encounter = (data.encounters ?? []).find((e) => e.encounter_id === encounterId);
      const entries = encounter?.entries ?? [];
      // Encounter scope only surfaces invoices linked to this encounter —
      // a patient-wide statement open elsewhere isn't actionable from the
      // visit-scoped view.
      const openInvoices = allOpenInvoices.filter(
        (inv) => inv.encounter_id === encounterId,
      );
      // Prefer preview's total_due_cents — it's authoritative (clamped, post-
      // adjustments, accounts for unallocated credits). Fall back to the
      // encounter group while preview is loading or absent. The clamp hides
      // over-collection, so a zero due with a negative ledger balance means
      // a credit — surface it (the hero must never say "Settled $0" on an
      // encounter holding the patient's money).
      const rawBalanceCents = encounter?.patient_balance_cents ?? 0;
      const previewDueCents = preview?.total_due_cents;
      const balanceCents =
        previewDueCents == null
          ? rawBalanceCents
          : previewDueCents === 0 && rawBalanceCents < 0
            ? rawBalanceCents
            : previewDueCents;
      return {
        isLoading: isLoading || isPreviewLoading,
      isPreviewLoading,
        patientName: data.patient_name,
        balanceCents,
        entries: [...entries].sort((a, b) => b.created_at.localeCompare(a.created_at)),
        activityLines:
          (encounter as { activity_lines?: ActivityLine[] } | undefined)
            ?.activity_lines ?? [],
        encounter,
        preview,
        openInvoiceIds: openInvoices.map((inv) => inv.invoice_id),
        openInvoices,
        staleInvoiceIds: extractStaleInvoiceIds([encounter]),
      };
    }

    // Patient scope — flatten all encounters' entries + account-level entries
    const flat: Array<LedgerEntryItem & { encounterLabel?: string | null }> = [];
    for (const enc of data.encounters ?? []) {
      const label = enc.service_date
        ? new Date(enc.service_date).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
          })
        : enc.encounter_type_label ?? null;
      for (const entry of enc.entries ?? []) {
        flat.push({ ...entry, encounterLabel: label ? `${label} visit` : null });
      }
    }
    for (const entry of data.account_level_entries ?? []) {
      flat.push({ ...entry, encounterLabel: null });
    }
    flat.sort((a, b) => b.created_at.localeCompare(a.created_at));

    return {
      isLoading,
      isPreviewLoading,
      patientName: data.patient_name,
      balanceCents: preview?.total_due_cents ?? data.summary?.total_balance_cents ?? 0,
      entries: flat,
      activityLines: [],
      openInvoiceIds: allOpenInvoices.map((inv) => inv.invoice_id),
      openInvoices: allOpenInvoices,
      staleInvoiceIds: extractStaleInvoiceIds(data.encounters ?? []),
      preview,
    };
  }, [data, encounterId, isLoading, isPreviewLoading, preview]);

  return result;
}

function extractStaleInvoiceIds(encounters: Array<EncounterLedgerGroup | undefined>): string[] {
  const ids: string[] = [];
  for (const encounter of encounters) {
    const reconciliation = (encounter as unknown as {
      patient_responsibility_reconciliation?: {
        stale_invoice_ids?: string[] | null;
      } | null;
    } | undefined)?.patient_responsibility_reconciliation;
    for (const id of reconciliation?.stale_invoice_ids ?? []) {
      ids.push(id);
    }
  }
  return Array.from(new Set(ids));
}
